import { useEffect, useState } from 'react';
import { Select, Input, Button, DatePicker, Drawer, Grid } from 'antd';
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { getAccountsApi } from '@http/accounts';
import { SEARCH_DEBOUNCE_MS, TRANSACTION_TYPE_OPTIONS } from './constants';
import { TransactionFilterValues } from './types';

const { RangePicker } = DatePicker;

interface TransactionFiltersProps {
  onApply: (filters: TransactionFilterValues) => void;
}

function TransactionFilters({ onApply }: TransactionFiltersProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const screens = Grid.useBreakpoint();
  const accountOptions = useAppSelector(state => state.accountsReducer.selectAccounts);

  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<TransactionFilterValues>({});

  const isMobile = !screens.md;

  useEffect(() => {
    dispatch(getAccountsApi());
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => onApply(draft), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft]);

  const setField = (field: keyof TransactionFilterValues, value?: string) => {
    setDraft(prev => ({ ...prev, [field]: value }));
  };

  const search = () => {
    onApply(draft);
    if (isMobile) {
      setIsOpen(false);
    }
  };

  const fields = (
    <div className='flex flex-col gap-5'>
      <div className='grid md:grid-cols-3 gap-5'>
        <Select
          className='w-full'
          size='large'
          allowClear
          placeholder={t('Select account')}
          options={accountOptions}
          value={draft.sending_ac}
          onChange={value => setField('sending_ac', value)}
        />
        <Select
          className='w-full'
          size='large'
          allowClear
          placeholder={t('Payment type')}
          options={TRANSACTION_TYPE_OPTIONS.map(option => ({ value: option.value, label: t(option.label) }))}
          value={draft.trn_type}
          onChange={value => setField('trn_type', value)}
        />
        <RangePicker
          className='w-full'
          size='large'
          allowClear
          format='DD.MM.YYYY'
          placeholder={[t('Start date'), t('End date')]}
          value={draft.from_date && draft.to_date ? [dayjs(draft.from_date), dayjs(draft.to_date)] : null}
          onChange={dates =>
            setDraft(prev => ({
              ...prev,
              from_date: dates?.[0]?.format('YYYY-MM-DD'),
              to_date: dates?.[1]?.format('YYYY-MM-DD'),
            }))
          }
          disabledDate={date => date.isAfter(dayjs(), 'day')}
        />
      </div>

      <div className='grid md:grid-cols-[repeat(4,1fr)_auto] gap-5'>
        <Input
          size='large'
          type='number'
          min={0}
          placeholder={`${t('From amount')}`}
          value={draft.from_amount}
          onChange={e => setField('from_amount', e.target.value)}
        />
        <Input
          size='large'
          type='number'
          min={0}
          placeholder={`${t('To amount')}`}
          value={draft.to_amount}
          onChange={e => setField('to_amount', e.target.value)}
        />
        <Input
          size='large'
          placeholder={`${t('Recipient account')}`}
          value={draft.receiver_acc}
          onChange={e => setField('receiver_acc', e.target.value)}
        />
        <Input
          size='large'
          placeholder={`${t('Recipient')}`}
          value={draft.receiver_name}
          onChange={e => setField('receiver_name', e.target.value)}
        />
        {isMobile ? (
          <div className='flex justify-end'>
            <Button size='large' type='primary' onClick={search}>
              {t('Confirm')}
            </Button>
          </div>
        ) : (
          <Button size='large' type='primary' icon={<SearchOutlined />} onClick={search}>
            {t('Search')}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className='my-5'>
      <Button type='primary' icon={<FilterOutlined />} onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? t('Hide filters') : t('Show filters')}
      </Button>

      {isMobile ? (
        <Drawer placement='bottom' title={false} closable={false} open={isOpen} onClose={() => setIsOpen(false)}>
          {fields}
        </Drawer>
      ) : (
        isOpen && <div className='mt-7'>{fields}</div>
      )}
    </div>
  );
}

export default TransactionFilters;
